import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { SCORING_API_BASE_URL } from '../../services/api';
import {
    getMergedRevisionStrategyFromStorage,
    saveRevisionStrategyToStorage,
} from '../../utils/qcmInitialLoad';

/** English level from average score on 0–10 scale (UI; independent of legacy backend labels). */
export function getEnglishLevelFromAvgScore(avg) {
    if (avg == null || Number.isNaN(Number(avg))) return '—';
    const s = Number(avg);
    if (s < 5.0) return 'Beginner';
    if (s < 6.5) return 'Developing';
    if (s < 7.5) return 'Proficient';
    if (s < 8.5) return 'Advanced';
    if (s < 9.5) return 'Expert';
    return 'Master';
}

/**
 * UserMetricsPanel - Composant réutilisable pour afficher les métriques utilisateur
 * Utilisé dans les versions desktop et mobile
 */
const UserMetricsPanel = ({ sessionContext, className = '' }) => {
    const [userMetrics, setUserMetrics] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [subdomainGapRemaining, setSubdomainGapRemaining] = useState(null);
    const [gapFetchState, setGapFetchState] = useState('idle'); // idle | loading | done | error
    const [serverPreparationStatus, setServerPreparationStatus] = useState('');

    const fetchGapFromDashboard = useCallback(async () => {
        if (!sessionContext?.user || !sessionContext?.exam) return;
        setGapFetchState('loading');
        try {
            const payload = {
                user_id: sessionContext.user,
                exam_id: sessionContext.exam,
                session_id: sessionContext.sessionId || '111222',
                makeup_sessions: [],
            };
            const response = await axios.post(
                `${SCORING_API_BASE_URL}/api/dashboard/compute`,
                payload,
                { timeout: 45000 }
            );
            const root = response.data || {};
            const dash = root.dashboard || root;
            const cons = dash.consolidation_time_estimate || root.consolidation_time_estimate;
            const gap =
                cons?.subdomain_questions_remaining ??
                root.subdomain_questions_remaining ??
                null;
            if (gap != null && Number.isFinite(Number(gap))) {
                setSubdomainGapRemaining(Math.max(0, Math.floor(Number(gap))));
            }
            setGapFetchState('done');
        } catch (e) {
            console.warn('UserMetricsPanel: dashboard/compute for gap failed', e?.message || e);
            setGapFetchState('error');
        }
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    useEffect(() => {
        const fetchUserMetrics = async () => {
            if (!sessionContext?.user || !sessionContext?.exam) {
                return;
            }

            setIsLoading(true);
            try {
                const payload = {
                    user_id: sessionContext.user,
                    exam_id: sessionContext.exam,
                    session_id: sessionContext.sessionId || '111222',
                };

                const response = await axios.post(
                    `${SCORING_API_BASE_URL}/api/get_score_averages`,
                    payload
                );

                if (response.data && response.data.success && response.data.metrics) {
                    const m = response.data.metrics;
                    setUserMetrics(m);
                    setServerPreparationStatus(
                        m.preparation_status != null && m.preparation_status !== ''
                            ? String(m.preparation_status)
                            : ''
                    );
                    const inlineGap =
                        m.subdomain_questions_remaining ??
                        m.consolidation_time_estimate?.subdomain_questions_remaining;
                    if (inlineGap != null && Number.isFinite(Number(inlineGap))) {
                        setSubdomainGapRemaining(Math.max(0, Math.floor(Number(inlineGap))));
                        setGapFetchState('done');
                    } else {
                        setSubdomainGapRemaining(null);
                        setGapFetchState('idle');
                        fetchGapFromDashboard();
                    }
                }
            } catch (error) {
                console.error('Error fetching user metrics:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserMetrics();
        const interval = setInterval(fetchUserMetrics, 30000);
        return () => clearInterval(interval);
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId, fetchGapFromDashboard]);

    /** Sync storage before getNextQCM so left_topics is never read stale (React batching). */
    const openUnderPracticedTopicsQcm = useCallback(() => {
        const uid = sessionContext?.user || 'anonymous_user';
        const exam = sessionContext?.exam;
        const sid = sessionContext?.sessionId || '111222';
        try {
            const prev = getMergedRevisionStrategyFromStorage(uid, exam, sid);
            const next = {
                ...prev,
                filterType: 'left_topics',
                studyMode: prev.studyMode || 'qcm',
            };
            saveRevisionStrategyToStorage(uid, exam, sid, next);
        } catch (_) {
            /* ignore */
        }
        try {
            window.dispatchEvent(new CustomEvent('nav_request', { detail: { page: 'qcm' } }));
        } catch (_) {
            /* ignore */
        }
        window.dispatchEvent(
            new CustomEvent('apprentissage:set-revision-strategy', {
                detail: { filterType: 'left_topics', studyMode: 'qcm' },
            })
        );
        window.dispatchEvent(new Event('requestNextQCM'));
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    const displayStatus = useMemo(() => {
        const base = { text: '', html: null, gapPracticeCta: false };
        if (!userMetrics) return base;
        const where = Number(userMetrics.validated_qcm_td60);
        const prep = Number(userMetrics.preparation_threshold);
        if (!Number.isFinite(where) || !Number.isFinite(prep)) {
            return { text: serverPreparationStatus || 'N/A', html: null, gapPracticeCta: false };
        }
        if (gapFetchState === 'loading' && subdomainGapRemaining === null) {
            return { text: serverPreparationStatus || 'Updating…', html: null, gapPracticeCta: false };
        }
        if (subdomainGapRemaining == null) {
            return { text: serverPreparationStatus || 'N/A', html: null, gapPracticeCta: false };
        }
        if (where >= prep && subdomainGapRemaining === 0) {
            return { text: 'Ready!', html: null, gapPracticeCta: false };
        }
        if (where >= prep && subdomainGapRemaining > 0) {
            return {
                text: '',
                html: (
                    <span className="text-purple-200 font-semibold">
                        Almost ready — {subdomainGapRemaining} sub-domain question
                        {subdomainGapRemaining === 1 ? '' : 's'} to consolidate.
                    </span>
                ),
                gapPracticeCta: true,
            };
        }
        return { text: serverPreparationStatus || 'N/A', html: null, gapPracticeCta: false };
    }, [userMetrics, serverPreparationStatus, subdomainGapRemaining, gapFetchState]);

    if (isLoading && !userMetrics) {
        return (
            <div className={`bg-purple-900/20 border border-purple-600/30 rounded-lg p-4 ${className}`}>
                <div className="text-center text-gray-400">Loading metrics...</div>
            </div>
        );
    }

    if (!userMetrics) {
        return (
            <div className={`bg-purple-900/20 border border-purple-600/30 rounded-lg p-4 ${className}`}>
                <div className="text-center text-gray-400">
                    No metrics available yet. Complete some QCMs to see your stats!
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-purple-900/20 border border-purple-600/30 rounded-lg p-4 ${className}`}>
            <div className="flex items-center mb-4">
                <svg
                    className="w-5 h-5 text-purple-400 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                </svg>
                <span className="text-lg font-semibold text-purple-300">Stats</span>
            </div>

            <div className="border-t border-purple-600/30 mb-4" />

            <div className="mb-4">
                <div className="text-sm text-purple-300 mb-3">Latest</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-purple-600/30">
                                <th className="text-left text-gray-300 py-2">Range</th>
                                <th className="text-left text-gray-300 py-2">Avg Score</th>
                                <th className="text-left text-gray-300 py-2">Validated QCM</th>
                                <th className="text-left text-gray-300 py-2 pr-6">Stability</th>
                                <th className="text-left text-gray-300 py-2 pl-3">Level</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="hover:bg-purple-900/10">
                                <td className="text-gray-400 py-1">10</td>
                                <td className="text-purple-200 font-semibold">
                                    {userMetrics?.avg_score_10 != null
                                        ? `${Number(userMetrics.avg_score_10).toFixed(1)}`
                                        : 'N/A'}
                                    /10
                                </td>
                                <td>
                                    <div className="text-purple-200 font-semibold mb-0.5">
                                        {userMetrics?.validated_qcm_10 != null
                                            ? Number(userMetrics.validated_qcm_10).toFixed(1)
                                            : 'N/A'}
                                        %
                                    </div>
                                    <div className="w-1/2 bg-gray-700 rounded-full h-1">
                                        <div
                                            className="bg-purple-400 h-1 rounded-full"
                                            style={{
                                                width: `${Math.min(100, userMetrics?.validated_qcm_10 || 0)}%`,
                                            }}
                                        />
                                    </div>
                                </td>
                                <td className="text-purple-200 text-xs pr-6">
                                    {userMetrics?.consistency_10 || 'N/A'}
                                </td>
                                <td className="text-purple-200 text-xs font-semibold pl-3">
                                    {getEnglishLevelFromAvgScore(userMetrics?.avg_score_10)}
                                </td>
                            </tr>
                            <tr className="hover:bg-purple-900/10">
                                <td className="text-gray-400 py-1">30</td>
                                <td className="text-purple-200 font-semibold">
                                    {userMetrics?.avg_score_30 != null
                                        ? `${Number(userMetrics.avg_score_30).toFixed(1)}`
                                        : 'N/A'}
                                    /10
                                </td>
                                <td>
                                    <div className="text-purple-200 font-semibold mb-0.5">
                                        {userMetrics?.validated_qcm_30 != null
                                            ? Number(userMetrics.validated_qcm_30).toFixed(1)
                                            : 'N/A'}
                                        %
                                    </div>
                                    <div className="w-1/2 bg-gray-700 rounded-full h-1">
                                        <div
                                            className="bg-purple-400 h-1 rounded-full"
                                            style={{
                                                width: `${Math.min(100, userMetrics?.validated_qcm_30 || 0)}%`,
                                            }}
                                        />
                                    </div>
                                </td>
                                <td className="text-purple-200 text-xs pr-6">
                                    {userMetrics?.consistency_30 || 'N/A'}
                                </td>
                                <td className="text-purple-200 text-xs font-semibold pl-3">
                                    {getEnglishLevelFromAvgScore(userMetrics?.avg_score_30)}
                                </td>
                            </tr>
                            <tr className="hover:bg-purple-900/10 bg-purple-900/20">
                                <td className="text-gray-400 py-1 font-bold">60</td>
                                <td className="text-purple-200 font-semibold">
                                    {userMetrics?.avg_score_td60 != null
                                        ? `${Number(userMetrics.avg_score_td60).toFixed(1)}`
                                        : 'N/A'}
                                    /10
                                </td>
                                <td>
                                    <div className="text-purple-200 font-semibold mb-0.5">
                                        {userMetrics?.validated_qcm_td60 != null
                                            ? Number(userMetrics.validated_qcm_td60).toFixed(1)
                                            : 'N/A'}
                                        %
                                    </div>
                                    <div className="w-1/2 bg-gray-700 rounded-full h-1">
                                        <div
                                            className="bg-purple-400 h-1 rounded-full"
                                            style={{
                                                width: `${Math.min(100, userMetrics?.validated_qcm_td60 || 0)}%`,
                                            }}
                                        />
                                    </div>
                                </td>
                                <td className="text-purple-200 text-xs pr-6">
                                    {userMetrics?.consistency_td60 || 'N/A'}
                                </td>
                                <td className="text-purple-200 text-xs font-semibold pl-3">
                                    {getEnglishLevelFromAvgScore(userMetrics?.avg_score_td60)}
                                </td>
                            </tr>
                            <tr className="hover:bg-purple-900/10">
                                <td className="text-gray-400 py-1">[61-120]</td>
                                <td className="text-purple-200 font-semibold">
                                    {userMetrics?.avg_score_td61_120 != null
                                        ? `${Number(userMetrics.avg_score_td61_120).toFixed(1)}`
                                        : 'N/A'}
                                    /10
                                </td>
                                <td>
                                    <div className="text-purple-200 font-semibold mb-0.5">
                                        {userMetrics?.validated_qcm_td61_120 != null
                                            ? Number(userMetrics.validated_qcm_td61_120).toFixed(1)
                                            : 'N/A'}
                                        %
                                    </div>
                                    <div className="w-1/2 bg-gray-700 rounded-full h-1">
                                        <div
                                            className="bg-blue-400 h-1 rounded-full"
                                            style={{
                                                width: `${Math.min(100, userMetrics?.validated_qcm_td61_120 || 0)}%`,
                                            }}
                                        />
                                    </div>
                                </td>
                                <td className="text-purple-200 text-xs pr-6">
                                    {userMetrics?.consistency_td61_120 || 'N/A'}
                                </td>
                                <td className="text-purple-200 text-xs font-semibold pl-3">
                                    {getEnglishLevelFromAvgScore(userMetrics?.avg_score_td61_120)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <div className="text-sm text-purple-300">Preparation settings</div>
                    <div className="text-xs text-gray-400">
                        Exam threshold:{' '}
                        <span className="text-purple-200 font-semibold">
                            {userMetrics?.validation_threshold != null
                                ? `${Number(userMetrics.validation_threshold).toFixed(1)}`
                                : 'N/A'}
                            %
                        </span>
                    </div>
                    <div className="text-xs text-gray-400">
                        Preparation threshold:{' '}
                        <span className="text-purple-200 font-semibold">
                            {userMetrics?.preparation_threshold != null
                                ? `${Number(userMetrics.preparation_threshold).toFixed(1)}`
                                : 'N/A'}
                            %
                        </span>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="text-sm text-purple-300">Forecast & status</div>

                    <div className="text-xs text-gray-400">
                        Where you are:{' '}
                        <span className="text-purple-200 font-semibold">
                            {userMetrics?.validated_qcm_td60 != null
                                ? Number(userMetrics.validated_qcm_td60).toFixed(1)
                                : 'N/A'}
                            %
                        </span>
                    </div>
                    {userMetrics?.validated_qcm_td60 != null &&
                        userMetrics?.preparation_threshold != null && (
                            <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                                <div
                                    className="bg-green-400 h-2 rounded-full transition-all"
                                    style={{
                                        width: `${Math.min(
                                            100,
                                            (userMetrics.validated_qcm_td60 /
                                                userMetrics.preparation_threshold) *
                                                100
                                        )}%`,
                                    }}
                                />
                            </div>
                        )}
                    {userMetrics?.preparation_threshold != null && (
                        <div className="text-xs text-gray-400">
                            Goal: {Number(userMetrics.preparation_threshold).toFixed(1)}%
                        </div>
                    )}

                    {subdomainGapRemaining != null && (
                        <div className="text-xs text-gray-400">
                            Remaining exam scope:{' '}
                            <span className="text-purple-200 font-semibold tabular-nums">
                                {subdomainGapRemaining} Q
                            </span>
                        </div>
                    )}

                    <div className="text-xs text-gray-400 mt-2">
                        Time Invested:{' '}
                        <span className="text-purple-200 font-semibold">
                            {userMetrics?.time_passed_hours != null
                                ? `${Number(userMetrics.time_passed_hours).toFixed(1)}h`
                                : 'N/A'}
                        </span>
                    </div>
                    <div className="text-xs text-gray-400">
                        Time to Ready:{' '}
                        <span className="text-purple-200 font-semibold">
                            {userMetrics?.time_left_hours != null ? (
                                `${Number(userMetrics.time_left_hours).toFixed(1)}h`
                            ) : userMetrics?.time_to_threshold_status ===
                              'calcul trop approximatif sans progression' ? (
                                <span className="text-orange-400">
                                    No progress detected - keep practicing!
                                </span>
                            ) : (
                                'N/A'
                            )}
                        </span>
                    </div>
                    {userMetrics?.time_passed_hours != null &&
                        userMetrics?.time_left_hours != null &&
                        userMetrics.time_passed_hours > 0 &&
                        userMetrics.time_passed_hours + userMetrics.time_left_hours > 0 && (
                            <>
                                <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                                    <div
                                        className="bg-blue-400 h-2 rounded-full transition-all"
                                        style={{
                                            width: `${
                                                (userMetrics.time_passed_hours /
                                                    (userMetrics.time_passed_hours +
                                                        userMetrics.time_left_hours)) *
                                                100
                                            }%`,
                                        }}
                                    />
                                </div>
                                <div className="text-xs text-gray-400">
                                    {(
                                        (userMetrics.time_passed_hours /
                                            (userMetrics.time_passed_hours +
                                                userMetrics.time_left_hours)) *
                                        100
                                    ).toFixed(0)}
                                    % complete -{' '}
                                    {(
                                        userMetrics.time_passed_hours + userMetrics.time_left_hours
                                    ).toFixed(1)}
                                    h estimated
                                </div>
                            </>
                        )}

                    <div className="text-xs text-gray-400">
                        Status:{' '}
                        {displayStatus.html ? (
                            displayStatus.html
                        ) : (
                            <span className="text-purple-200 font-semibold">{displayStatus.text}</span>
                        )}
                    </div>
                    {displayStatus.gapPracticeCta && (
                        <button
                            type="button"
                            onClick={openUnderPracticedTopicsQcm}
                            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-purple-500/35 bg-purple-950/40 px-3 py-3 text-sm font-semibold text-purple-100 shadow-md transition-colors hover:border-amber-400/45 hover:bg-purple-900/50 hover:text-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 active:scale-[0.99]"
                        >
                            <svg
                                className="h-5 w-5 shrink-0 text-amber-300/90"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                />
                            </svg>
                            <span className="text-left leading-snug">
                                Practice a QCM on your least-covered topics
                            </span>
                            <svg
                                className="h-5 w-5 shrink-0 opacity-80"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                                />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserMetricsPanel;
